'use client';

import { useState, useEffect, useMemo } from 'react';
import { OptimizedImage } from '@/components/optimized-image';
import { cn } from '@/lib/utils';
import { colorFromString, getInitials, generateChannelCells } from '@/lib/avatar';

const AGENT_FACE = 'M26.88,13.679c0-4.02-3.259-7.279-7.279-7.279h-7.279v.002c-3.984.042-7.201,3.283-7.201,7.277s3.217,7.235,7.201,7.277v.002h7.279c4.02,0,7.279-3.259,7.279-7.279Z';

function isCustomAvatar(src: string | null | undefined): src is string {
  if (!src) return false;
  if (src.startsWith('/api/avatar')) return false;
  if (src.startsWith('data:image/svg+xml')) return false;
  if (src.includes('dicebear.com')) return false;
  return true;
}

export interface AvatarProps {
  seed: string;
  type: 'agent' | 'human' | 'channel';
  src?: string | null;
  className?: string;
}

function AgentDefault({ seed }: { seed: string }) {
  const color = colorFromString(seed);
  return (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
      <circle cx="16" cy="16" r="16" fill={color} />
      <path d={AGENT_FACE} fill="#000" />
      <circle cx="12.312" cy="13.712" r="1.357" fill="#fff" />
      <circle cx="19.635" cy="13.712" r="1.357" fill="#fff" />
    </svg>
  );
}

function HumanDefault({ seed }: { seed: string }) {
  const color = colorFromString(seed);
  const initials = getInitials(seed);
  return (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
      <circle cx="16" cy="16" r="16" fill={color} />
      <text
        x="16"
        y="16"
        textAnchor="middle"
        dominantBaseline="central"
        fill="#fff"
        fontSize="13"
        fontWeight="600"
        fontFamily="system-ui,-apple-system,sans-serif"
      >
        {initials}
      </text>
    </svg>
  );
}

function ChannelDefault({ seed }: { seed: string }) {
  const color = colorFromString(seed);
  const cells = useMemo(() => generateChannelCells(seed), [seed]);
  return (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
      <rect width="32" height="32" fill={color} />
      {cells.map((c, i) => (
        <rect key={i} x={c.x} y={c.y} width={c.w} height={c.h} fill="rgba(255,255,255,0.45)" />
      ))}
    </svg>
  );
}

export function Avatar({ seed, type, src, className }: AvatarProps) {
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [src]);

  const shape = type === 'channel' ? 'rounded-md' : 'rounded-full';

  if (isCustomAvatar(src) && !imgError) {
    return (
      <div className={cn(shape, 'overflow-hidden flex-shrink-0 bg-muted relative', className)}>
        <OptimizedImage
          src={src}
          alt={seed}
          fill
          className="object-cover"
          sizes="64px"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div className={cn(shape, 'overflow-hidden flex-shrink-0', className)}>
      {type === 'agent' ? (
        <AgentDefault seed={seed} />
      ) : type === 'channel' ? (
        <ChannelDefault seed={seed} />
      ) : (
        <HumanDefault seed={seed} />
      )}
    </div>
  );
}
